import { useState } from "react";

import type { TrackResponse } from "@sirdab/returns-shared";
import { Alert, Button, Input } from "@sirdab/ui";

import { failureMessageKey, useApi, type ApiFailure } from "../api";
import { ErrorState, Field } from "../components";
import { toWirePhone } from "../format/phone";
import { useMessages, usePortalCopy } from "../i18n";
import { canCancelLine } from "./lineState";
import { OfflineNote } from "./shared";

/**
 * Withdrawing a return, from the tracking page (docs/06 `POST /api/public/returns/cancel`).
 *
 * Four things this panel is careful about.
 *
 * 1. **Whether to offer it at all is `@sirdab/returns-core`'s decision, not this file's.** Every
 *    gate here runs through `canCancelLine` → `isCancellableState`. A second copy of the
 *    state list in the UI drifts from the first, which is exactly what already happened in
 *    the API layer and had to be removed. The server cancels the WHOLE return and refuses
 *    unless every line is still cancellable, so the button is offered on the same
 *    condition — anything looser would be an offer we know the server will reject.
 * 2. **A return that can no longer be withdrawn says so.** When some lines are still
 *    cancellable but others have moved on, the absent button is explained rather than
 *    silently missing. The note is informational, never a refusal, and carries no blame.
 * 3. **Cancelling is confirmed before it happens, and the confirmation says it is final.**
 *    Core does not allow re-opening — a change of mind is a new return, not a rewind — so
 *    she is told that before she acts rather than discovering it after.
 * 4. **A cancelled line is never a rejection.** On success the lines are projected to
 *    `cancelled` and any `rejectionReason` is dropped on the way through, so nothing on
 *    the page can render her own withdrawal as a merchant refusal. `rejectionPanelVisible`
 *    still gates on state; this only makes sure the state it sees is the true one.
 *
 * Nothing here promises money moving or a date for it (ADR-003), and no copy explains a
 * policy decision: when the server refuses with a `reasonText`, that text is what renders.
 */

export interface CancelPanelProps {
  merchantSlug: string;
  /**
   * The phone that opened this return. Null when a track link opened it: the server still
   * wants the phone to cancel, so the confirm step asks for it.
   */
  phone: string | null;
  data: TrackResponse;
  /** Replaces the tracked data with the post-cancellation view. */
  onCancelled: (next: TrackResponse) => void;
  /** Re-reads the return from the API. The way out of a stale-state refusal. */
  onRefresh: () => void;
}

/**
 * What tracking shows the instant the server confirms `cancelled: true`, without waiting
 * for a second round trip. Grades and received quantities are facts and stay; the
 * rejection reason is dropped, because after this the line is not rejected.
 */
export function projectCancelled(data: TrackResponse): TrackResponse {
  return {
    ...data,
    lines: data.lines.map((line) => {
      if (!canCancelLine(line.state)) return line;
      const rest = { ...line };
      delete rest.rejectionReason;
      return { ...rest, state: "cancelled" as const };
    }),
  };
}

type Phase = "idle" | "confirming" | "busy" | "done";

export function CancelPanel({
  merchantSlug,
  phone,
  data,
  onCancelled,
  onRefresh,
}: CancelPanelProps): React.JSX.Element | null {
  const api = useApi();
  const { t, text } = useMessages();
  const c = usePortalCopy();
  const [phase, setPhase] = useState<Phase>("idle");
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [typedPhone, setTypedPhone] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);

  const lines = data.lines;
  const allCancellable =
    lines.length > 0 && lines.every((line) => canCancelLine(line.state));
  const someCancellable = lines.some((line) => canCancelLine(line.state));

  if (phase === "done") {
    return <Alert>{t("cancel.done")}</Alert>;
  }

  if (!allCancellable) {
    // Explained, not silently absent — but only while something on this return was still
    // live. A fully settled return needs no note; its states already say what happened.
    return someCancellable ? (
      <Alert>{t("cancel.unavailable_note")}</Alert>
    ) : null;
  }

  const askPhone = phone === null;
  const phoneError =
    askPhone && phoneTouched && typedPhone.trim() === ""
      ? t("validation.phone_required")
      : undefined;

  async function cancel(): Promise<void> {
    if (askPhone && typedPhone.trim() === "") {
      setPhoneTouched(true);
      return;
    }
    setFailure(null);
    setPhase("busy");
    const result = await api.cancelReturn(data.reference, {
      merchantSlug,
      phone: phone ?? toWirePhone(typedPhone),
    });
    if (!result.ok) {
      // Every outcome lands here with a way forward. A `422 cancel_not_allowed` is a real
      // race — the warehouse may have scanned the parcel while she was looking at the page
      // — and it is the server's decision to explain, so its own `reasonText` renders.
      setFailure(result);
      setPhase("confirming");
      return;
    }
    setPhase("done");
    onCancelled(projectCancelled(data));
  }

  // The server's words first, then the catalogue's copy for the code. Anything we cannot
  // name says plainly that the return was not cancelled.
  function describeFailure(value: ApiFailure): string {
    if (value.kind !== "response") return t("cancel.failed_note");
    if (value.reasonText !== null) return text(value.reasonText);
    const key = failureMessageKey(value);
    return key === "error.internal" ? t("cancel.failed_note") : t(key);
  }

  const failureMessage = failure === null ? null : describeFailure(failure);

  // A 422 means the state she is looking at is stale, so the way out is a fresh status,
  // not another identical cancel. Anything else left the return untouched: retry the cancel.
  const stale = failure !== null && failure.kind === "response";

  if (phase === "idle") {
    return (
      <Button
        type="button"
        variant="destructive"
        size="touch"
        className="w-full"
        onClick={() => setPhase("confirming")}
      >
        {t("action.cancel_return")}
      </Button>
    );
  }

  return (
    <section
      data-cancel-panel="confirm"
      className="border-border flex flex-col gap-3 rounded-lg border p-3"
    >
      <p className="text-label text-foreground">{t("cancel.confirm_title")}</p>
      <p className="text-body text-foreground">{t("cancel.confirm_body")}</p>
      <p className="text-caption-quiet text-muted-foreground">
        {t("cancel.scope_note")}
      </p>

      <OfflineNote />

      {askPhone ? (
        <Field
          label={t("field.phone.label")}
          hint={c("cancel.phone_hint")}
          error={phoneError}
          required
        >
          {(props) => (
            <Input
              {...props}
              size="touch"
              dir="ltr"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={typedPhone}
              placeholder={t("field.phone.placeholder")}
              onChange={(event) => setTypedPhone(event.target.value)}
            />
          )}
        </Field>
      ) : null}

      {failureMessage !== null ? (
        <ErrorState
          message={failureMessage}
          retryLabel={stale ? t("action.refresh_status") : t("action.retry")}
          onRetry={() => {
            if (stale) {
              // The server told us the line moved on, so the status on screen is stale.
              // Re-read it rather than re-asserting a cancel we now know it will refuse.
              setFailure(null);
              setPhase("idle");
              onRefresh();
              return;
            }
            void cancel();
          }}
        />
      ) : null}

      <Button
        type="button"
        variant="destructive"
        size="touch"
        className="w-full"
        loading={phase === "busy"}
        onClick={() => void cancel()}
      >
        {t("action.cancel_confirm")}
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="touch"
        className="w-full"
        disabled={phase === "busy"}
        onClick={() => {
          setFailure(null);
          setPhase("idle");
        }}
      >
        {t("action.keep_return")}
      </Button>
    </section>
  );
}
